'use client';

import { getFullName } from '@/models/user/user';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { LogOut, Mail } from 'lucide-react';

import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

import { Avatar, toast } from '@/shared/components';
import { AvatarWithDetails } from '@/shared/components/avatar';
import { APP_ENVS } from '@/shared/constants/envs';
import { QUERY_KEY_INITIALS } from '@/shared/constants/query-key-initials';
import { getUserDetails } from '@/shared/services/user';
import { resetMixpanel } from '@/shared/utils/mixpanel-utils';

import { cn } from '@/lib/utils';

type LoggedInUserMenuProps = {
	onLogoutSuccess?: () => void | Promise<void>;
	className?: string;
	avatarClassName?: string;
};

/**
 * A self-contained avatar + dropdown showing the currently logged-in user's
 * details with support email copy and logout. Renders nothing when the user
 * is not authenticated. Uses the shared `GET_USER_DETAILS` query cache so it
 * integrates seamlessly with any existing user-details query on the same page.
 */
export function LoggedInUserMenu({
	onLogoutSuccess,
	className,
	avatarClassName,
}: LoggedInUserMenuProps) {
	const queryClient = useQueryClient();
	const userDetailsQueryKey = [QUERY_KEY_INITIALS.GET_USER_DETAILS];

	const { data: userDetails } = useQuery({
		queryKey: userDetailsQueryKey,
		queryFn: getUserDetails,
	});

	const { mutate: logout, isPending: isLoadingLogout } = useMutation({
		mutationFn: () => fetch('/logout', { method: 'POST' }),
		onSuccess: async () => {
			resetMixpanel();
			queryClient.setQueryData(userDetailsQueryKey, null);
			await onLogoutSuccess?.();
		},
		onError: (error) => {
			console.error('Logout failed:', error);
			toast({
				title: 'Something went wrong, Please try again',
				variant: 'destructive',
			});
		},
	});

	const handleCopyEmail = async () => {
		try {
			await navigator.clipboard.writeText(APP_ENVS.COMPANY_SUPPORT_EMAIL ?? '');
			toast({ title: 'Copied to clipboard', variant: 'success' });
		} catch {
			toast({ title: 'Failed to copy email', variant: 'destructive' });
		}
	};

	if (!userDetails) return null;

	return (
		<DropdownMenu>
			<DropdownMenuTrigger asChild>
				<div className={cn('flex cursor-pointer items-center justify-center', className)}>
					<Avatar
						name={getFullName(userDetails)}
						imageUrl={userDetails.image}
						className={avatarClassName}
					/>
				</div>
			</DropdownMenuTrigger>
			<DropdownMenuContent
				align="end"
				className="ml-10 min-w-64"
				onCloseAutoFocus={(e) => e.preventDefault()}
			>
				<DropdownMenuLabel className="p-0">
					<AvatarWithDetails
						className="px-2 py-3"
						name={getFullName(userDetails)}
						email={userDetails.email ?? ''}
						imageUrl={userDetails.image}
						medium
					/>
				</DropdownMenuLabel>
				<DropdownMenuSeparator />
				<DropdownMenuItem
					className="flex gap-2"
					onClick={handleCopyEmail}
				>
					<Mail className="h-4 w-4" />
					{APP_ENVS.COMPANY_SUPPORT_EMAIL}
				</DropdownMenuItem>
				<DropdownMenuItem
					className="flex gap-2"
					onClick={() => logout()}
					disabled={isLoadingLogout}
				>
					<LogOut className="h-4 w-4" />
					Log out
				</DropdownMenuItem>
			</DropdownMenuContent>
		</DropdownMenu>
	);
}
